import { jsPDF } from 'jspdf';

// ============================================================================
// USER LEADS REPORT — PDF generator
// Renders the same sections the mobile app shows (Lead Overview,
// Status Breakdown, Status Movement, Call Summary, Follow-ups, Attendance,
// Activities, Login & Device) as a crisp, text-based, multi-page A4 PDF.
// Input: `detail` = response.data from GET /api/reports/users/:id
// ============================================================================

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const fmtDate = (val) => {
  if (!val) return '';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return String(val);
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

const ACTIVITY_LABELS = {
  status_update: 'Status Updates',
  call: 'Calls',
  note: 'Notes',
  followup: 'Follow-ups',
  whatsapp: 'WhatsApp',
  email: 'Emails',
  sms: 'SMS',
  assignment: 'Assignments',
  meeting: 'Meetings',
  site_visit: 'Site Visits',
};

const labelize = (k) =>
  ACTIVITY_LABELS[k] || String(k).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const hexToRgb = (hex) => {
  if (!hex || typeof hex !== 'string') return [148, 163, 184];
  const m = hex.replace('#', '').match(/^([0-9a-f]{6})$/i);
  if (!m) return [148, 163, 184];
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const slug = (name) =>
  (name || 'user').trim().replace(/[^a-z0-9]+/gi, '_').replace(/^_|_$/g, '').slice(0, 40) || 'user';

export const generateUserReportPDF = (detail) => {
  if (!detail || !detail.user) throw new Error('No report data');

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
  const PAGE_W = 210;
  const PAGE_H = 297;
  const M = 14;
  const contentW = PAGE_W - 2 * M;
  let y = 22;

  const ensureSpace = (h) => {
    if (y + h > PAGE_H - 16) {
      pdf.addPage();
      y = M + 6;
    }
  };

  const sectionHeader = (title) => {
    ensureSpace(16);
    y += 2;
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    pdf.setTextColor(15, 23, 42);
    pdf.text(title, M, y);
    y += 2.5;
    pdf.setDrawColor(226, 232, 240);
    pdf.setLineWidth(0.3);
    pdf.line(M, y, PAGE_W - M, y);
    y += 7;
  };

  // Bordered stat cards, 3 per row
  const cardGrid = (items, perRow = 3) => {
    const gap = 4;
    const cardW = (contentW - gap * (perRow - 1)) / perRow;
    const cardH = 18;
    items.forEach((it, i) => {
      const col = i % perRow;
      if (col === 0) ensureSpace(cardH + gap);
      const x = M + col * (cardW + gap);
      pdf.setDrawColor(226, 232, 240);
      pdf.setLineWidth(0.3);
      pdf.roundedRect(x, y, cardW, cardH, 2, 2, 'S');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(16);
      pdf.setTextColor(15, 23, 42);
      pdf.text(String(it.value), x + cardW / 2, y + 9, { align: 'center' });
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8.5);
      pdf.setTextColor(100, 116, 139);
      pdf.text(it.label, x + cardW / 2, y + 14.5, { align: 'center' });
      if (col === perRow - 1 || i === items.length - 1) y += cardH + gap;
    });
  };

  // Label-left / value-right list with separators (optional color dot)
  const listRows = (rows) => {
    rows.forEach((r) => {
      ensureSpace(9);
      let lx = M;
      if (r.color) {
        pdf.setFillColor(...hexToRgb(r.color));
        pdf.circle(M + 1.4, y - 1.3, 1.4, 'F');
        lx = M + 5;
      }
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(51, 65, 85);
      pdf.text(String(r.label), lx, y);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(15, 23, 42);
      pdf.text(String(r.value), PAGE_W - M, y, { align: 'right' });
      y += 3;
      pdf.setDrawColor(241, 245, 249);
      pdf.setLineWidth(0.2);
      pdf.line(M, y, PAGE_W - M, y);
      y += 6;
    });
  };

  const u = detail.user;
  const name = (u.full_name || u.username || 'User').trim();
  const subtitle = u.designation || u.role_name || '';
  const sd = detail.dateRange?.start_date;
  const ed = detail.dateRange?.end_date;

  // ── Header ──────────────────────────────────────────────────────────────
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(22);
  pdf.setTextColor(15, 23, 42);
  pdf.text(name, M, y);
  y += 7;
  if (subtitle) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(11);
    pdf.setTextColor(100, 116, 139);
    pdf.text(subtitle, M, y);
    y += 7;
  }
  const pill = `${fmtDate(sd)} - ${fmtDate(ed)}`;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(9.5);
  const pw = pdf.getTextWidth(pill) + 8;
  pdf.setFillColor(245, 240, 225);
  pdf.roundedRect(M, y - 4.5, pw, 7, 3.5, 3.5, 'F');
  pdf.setTextColor(146, 108, 20);
  pdf.text(pill, M + 4, y);
  y += 12;

  // ── Lead Overview ───────────────────────────────────────────────────────
  const leads = detail.leads || {};
  sectionHeader('Lead Overview');
  cardGrid([
    { value: leads.assigned || 0, label: 'Total Assigned (All Time)' },
    { value: leads.created || 0, label: 'Created (Selected Dates)' },
    { value: leads.newAssignments || 0, label: 'New Assigned (Selected Dates)' },
  ]);

  // ── Lead Status Breakdown ───────────────────────────────────────────────
  const breakdown = leads.statusBreakdown || [];
  if (breakdown.length) {
    sectionHeader('Current Status of All Assigned Leads');
    listRows(
      breakdown.map((s) => ({
        label: s.status_name || 'Unassigned',
        value: s.count || 0,
        color: s.status_color,
      }))
    );
  }

  // ── Status Movement ─────────────────────────────────────────────────────
  const movement = leads.statusMovement || [];
  if (movement.length) {
    sectionHeader('Status Movement (Selected Dates)');
    listRows(
      movement.map((m) => ({
        label: `${m.from || 'New / Unset'} → ${m.to || 'Unknown'}`,
        value: m.count || 0,
      }))
    );
  }

  // ── Call Summary ────────────────────────────────────────────────────────
  const calls = detail.calls || {};
  sectionHeader('Call Summary');
  cardGrid([
    { value: calls.total || 0, label: 'Total' },
    { value: calls.outgoing || 0, label: 'Outgoing' },
    { value: calls.incoming || 0, label: 'Incoming' },
    { value: calls.missed || 0, label: 'Missed' },
  ]);

  // ── Follow-ups ──────────────────────────────────────────────────────────
  const fu = detail.followups || {};
  sectionHeader('Follow-ups');
  cardGrid([
    { value: fu.total || 0, label: 'Total' },
    { value: fu.completed || 0, label: 'Completed' },
    { value: fu.pending || 0, label: 'Pending' },
    { value: fu.cancelled || 0, label: 'Cancelled' },
    { value: fu.overdue || 0, label: 'Overdue' },
  ]);

  // ── Attendance ──────────────────────────────────────────────────────────
  const att = detail.attendance || {};
  sectionHeader('Attendance');
  cardGrid([
    { value: att.presentDays || 0, label: 'Present' },
    { value: att.absentDays || 0, label: 'Absent' },
    { value: att.halfdayDays || 0, label: 'Half Day' },
    { value: att.leaveDays || 0, label: 'Leave' },
  ]);

  // ── Activities ──────────────────────────────────────────────────────────
  const act = detail.activities || {};
  const actEntries = Object.entries(act.breakdown || {});
  if (actEntries.length) {
    sectionHeader(`Activities (${act.total || 0})`);
    listRows(actEntries.map(([k, v]) => ({ label: labelize(k), value: v })));
  }

  // ── Login & Device ──────────────────────────────────────────────────────
  const login = detail.login || {};
  sectionHeader('Login & Device');
  listRows([
    { label: 'Total Logins', value: login.totalLogins || 0 },
    { label: 'Device', value: login.lastDevice || '—' },
    { label: 'Login Location', value: login.lastLoginAddress || 'Not allowed' },
  ]);

  // ── Footer ──────────────────────────────────────────────────────────────
  ensureSpace(14);
  y += 6;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8.5);
  pdf.setTextColor(148, 163, 184);
  pdf.text(`Generated on ${fmtDate(new Date())}`, PAGE_W / 2, y, { align: 'center' });

  pdf.save(`report_${slug(name)}_${sd || ''}.pdf`);
  return pdf;
};
